import { IDentalChart } from '@dataTypes/chartingFeature.types'

export interface ResolveActiveDentalChartParams {
  selectedDentalChart: IDentalChart | null
  createDentalChart: IDentalChart
  dentalCharts: IDentalChart[]
  showExistingConditions: boolean
  patientId?: string
  /** True after Add chart / reset — use empty create buffer instead of latest history. */
  preferCreateDentalChart?: boolean
}

export const isExplicitDentalHistorySelection = (
  selectedDentalChart: IDentalChart | null,
  createDentalChart: IDentalChart,
): boolean =>
  Boolean(
    selectedDentalChart?.chart_id &&
      selectedDentalChart.chart_id !== createDentalChart.chart_id,
  )

/** History Edit aligned create + selected buffers on the same persisted chart. */
export const isAlignedPersistedDentalEditSession = (
  selectedDentalChart: IDentalChart | null,
  createDentalChart: IDentalChart,
): boolean => {
  const chartId = createDentalChart.chart_id?.trim() ?? ''
  return Boolean(chartId && selectedDentalChart?.chart_id?.trim() === chartId)
}

/** Chart id whose history-row Edit action should be disabled (active edit session only). */
export const getAlignedDentalEditChartId = (
  selectedDentalChart: IDentalChart | null,
  createDentalChart: IDentalChart,
  preferCreateDentalChart = false,
): string => {
  if (preferCreateDentalChart) {
    return ''
  }
  if (
    !isAlignedPersistedDentalEditSession(selectedDentalChart, createDentalChart)
  ) {
    return ''
  }
  return createDentalChart.chart_id?.trim() ?? ''
}

export const getLatestDentalChartForPatient = (
  dentalCharts: IDentalChart[],
  patientId: string,
): IDentalChart | null =>
  dentalCharts.find((chart) => chart.patient_id === patientId) ?? null

export const resolveActiveDentalChart = ({
  selectedDentalChart,
  createDentalChart,
  dentalCharts,
  showExistingConditions,
  patientId,
  preferCreateDentalChart = false,
}: ResolveActiveDentalChartParams): IDentalChart => {
  if (
    isExplicitDentalHistorySelection(selectedDentalChart, createDentalChart)
  ) {
    return selectedDentalChart as IDentalChart
  }

  const createChartId = createDentalChart.chart_id?.trim() ?? ''
  const selectedChartId = selectedDentalChart?.chart_id?.trim() ?? ''

  if (
    preferCreateDentalChart ||
    (createChartId &&
      (!selectedDentalChart || selectedChartId === createChartId))
  ) {
    return createDentalChart
  }

  if (showExistingConditions && patientId) {
    const latestChart = getLatestDentalChartForPatient(dentalCharts, patientId)
    if (latestChart) {
      return latestChart
    }
  }

  return createDentalChart
}

export const isPersistedDentalChart = (
  chart: IDentalChart,
  createDentalChart: IDentalChart,
): boolean =>
  Boolean(chart.chart_id && chart.chart_id !== createDentalChart.chart_id)
